import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Button, Chip, Collapse, Divider, Stack, Tooltip } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import ViewInArOutlinedIcon from '@mui/icons-material/ViewInArOutlined';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import OpenWithOutlinedIcon from '@mui/icons-material/OpenWithOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import GridOffOutlinedIcon from '@mui/icons-material/GridOffOutlined';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { Responsive, Layout, Layouts, WidthProvider } from 'react-grid-layout';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { ChartEditorComponentForm } from './ChartEditorComponentForm';
import { useFormik } from 'formik';
import { ChartItemComponent, getChartIconByType } from './ChartItemComponent';
import { ChartType, CHART_GRID_CONFIG } from 'constant/ChartViewConstant';
import { debounce } from 'lodash';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import 'assets/css/chart.css';

const ResponsiveGridLayout = WidthProvider(Responsive);
const closeIconStyle = { color: '#98A2AE', cursor: 'pointer' };

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction='up' ref={ref} {...props} />;
});

export type GaugeChartParams = {
  min: number;
  max: number;
  color: string;
  label: string | null;
};

export type ChartItemComponentTypes = {
  id: string;
  name: string;
  chartType: string;
  description: string;
  xAxis: AutoCompleteItem;
  yAxis: AutoCompleteItem;
  gaugeParams: GaugeChartParams[];
};

type ChartEditorProps = {
  onClose?: () => void;
  open: boolean;
  chartViewName: string;
  onChanges?: (data: { charts: ChartItemComponentTypes[]; layouts: Layouts }) => void;
  initialValues?: {
    charts: ChartItemComponentTypes[];
    layouts: Layouts;
  };
};

function ChartTypeItem(props: {
  icon: React.ReactNode;
  title: string;
  chartType: string;
  disabled: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
}) {
  const handleOnDragStart = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (props.disabled) {
        event.preventDefault();
        return;
      }
      event.dataTransfer.setData(
        'application/reactgrid',
        JSON.stringify({
          chartType: props.chartType,
          name: 'Untitled Chart',
          description: null,
          xAxis: null,
          yAxis: null,
          gaugeParams:
            props.chartType === ChartType.GAUGE
              ? [
                  { min: 0, max: 100, color: '#42BB93', label: null },
                  { min: 101, max: 200, color: '#FFC107', label: null },
                  { min: 201, max: 300, color: '#F44336', label: null },
                ]
              : [],
        }),
      );
      props.onDragStart();
    },
    [props],
  );

  const handleOnDragEnd = useCallback(() => {
    props.onDragEnd();
  }, [props]);

  return (
    <Tooltip title={props.disabled ? 'Maximum charts reached' : 'Drag to canvas'} arrow>
      <Stack
        direction='row'
        alignItems='center'
        gap={0.75}
        draggable={!props.disabled}
        onDragStart={handleOnDragStart}
        onDragEnd={handleOnDragEnd}
        className={props.disabled ? 'chart-type-disabled' : 'chart-type-draggable'}
        sx={{
          px: 1.25,
          py: 0.5,
          borderRadius: 1.5,
          border: '1px solid #e0e3e8',
          bgcolor: '#fff',
          cursor: props.disabled ? 'not-allowed' : 'grab',
          userSelect: 'none',
          transition: 'all 0.15s ease',
          ...(!props.disabled && {
            '&:hover': { borderColor: '#42BB93', bgcolor: '#f0faf6' },
          }),
        }}
      >
        {props.icon}
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#555', whiteSpace: 'nowrap' }}>
          {props.title}
        </Typography>
      </Stack>
    </Tooltip>
  );
}

const CHART_TYPE_OPTIONS = [
  {
    title: 'Line Chart',
    type: ChartType.LINE,
    icon: getChartIconByType(ChartType.LINE),
  },
  {
    title: 'Bar Chart',
    type: ChartType.BAR,
    icon: getChartIconByType(ChartType.BAR),
  },
  {
    title: 'Pie Chart',
    type: ChartType.PIE,
    icon: getChartIconByType(ChartType.PIE),
  },
  {
    title: 'Row Chart',
    type: ChartType.ROW,
    icon: getChartIconByType(ChartType.ROW),
  },
  {
    title: 'Gauge Chart',
    type: ChartType.GAUGE,
    icon: getChartIconByType(ChartType.GAUGE),
  },
];

const MAX_ITEMS = 4;

// ── Grid Overlay with Rulers ──

const RULER_SIZE = 24;
const GRID_LINE_COLOR = 'rgba(59, 71, 151, 0.08)';
const RULER_BG = '#f0f1f5';
const RULER_TEXT_COLOR = '#8890a4';
const RULER_TICK_COLOR = 'rgba(59, 71, 151, 0.2)';

/**
 * Calculates column positions using the same formula as react-grid-layout:
 *   colWidth = (containerWidth - padding*2 - margin*(cols-1)) / cols
 *   colX[i]  = padding + i * (colWidth + margin)
 */
function getColPositions(containerWidth: number, colCount: number, margin: number, padding: number) {
  const usableWidth = containerWidth - padding * 2 - margin * (colCount - 1);
  const colWidth = usableWidth / colCount;
  return Array.from({ length: colCount }, (_, i) => ({
    x: padding + i * (colWidth + margin),
    width: colWidth,
  }));
}

interface GridOverlayProps {
  colCount: number;
  rowHeight: number;
  rowCount: number;
  margin: number;
  padding: number;
  containerWidth: number;
}

function TopRuler({ colCount, margin, padding, containerWidth }: GridOverlayProps) {
  const cols = getColPositions(containerWidth, colCount, margin, padding);

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: RULER_SIZE,
        width: containerWidth,
        height: RULER_SIZE,
        background: RULER_BG,
        zIndex: 5,
        borderBottom: `1px solid ${RULER_TICK_COLOR}`,
        userSelect: 'none',
      }}
    >
      {cols.map((col, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: col.x,
            width: col.width,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 9,
            fontWeight: 600,
            color: RULER_TEXT_COLOR,
            borderLeft: `1px solid ${RULER_TICK_COLOR}`,
            borderRight: i === colCount - 1 ? `1px solid ${RULER_TICK_COLOR}` : 'none',
            boxSizing: 'border-box',
          }}
        >
          {i + 1}
        </div>
      ))}
    </div>
  );
}

function LeftRuler({ rowHeight, rowCount, margin, padding }: GridOverlayProps) {
  return (
    <div
      style={{
        position: 'absolute',
        top: RULER_SIZE,
        left: 0,
        bottom: 0,
        width: RULER_SIZE,
        background: RULER_BG,
        zIndex: 5,
        borderRight: `1px solid ${RULER_TICK_COLOR}`,
        userSelect: 'none',
        overflow: 'hidden',
      }}
    >
      {/* offset for containerPadding top */}
      <div style={{ height: padding }} />
      {Array.from({ length: rowCount }, (_, i) => (
        <div
          key={i}
          style={{
            height: rowHeight + margin,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 9,
            fontWeight: 600,
            color: RULER_TEXT_COLOR,
            borderBottom: `1px solid ${RULER_TICK_COLOR}`,
            boxSizing: 'border-box',
          }}
        >
          {i + 1}
        </div>
      ))}
    </div>
  );
}

function RulerCorner() {
  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: RULER_SIZE,
        height: RULER_SIZE,
        background: RULER_BG,
        zIndex: 6,
        borderRight: `1px solid ${RULER_TICK_COLOR}`,
        borderBottom: `1px solid ${RULER_TICK_COLOR}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <GridOnOutlinedIcon sx={{ fontSize: 12, color: RULER_TEXT_COLOR }} />
    </div>
  );
}

function GridLines({ colCount, rowHeight, rowCount, margin, padding, containerWidth }: GridOverlayProps) {
  const cols = getColPositions(containerWidth, colCount, margin, padding);
  const totalHeight = padding + rowCount * (rowHeight + margin);

  return (
    <svg
      style={{
        position: 'absolute',
        top: RULER_SIZE,
        left: RULER_SIZE,
        width: containerWidth,
        height: totalHeight,
        pointerEvents: 'none',
        zIndex: 1,
      }}
    >
      {/* Column lines — at left and right edge of each column */}
      {cols.map((col, i) => (
        <React.Fragment key={`col-${i}`}>
          <line
            x1={col.x}
            y1={padding}
            x2={col.x}
            y2={totalHeight}
            stroke={GRID_LINE_COLOR}
            strokeWidth={1}
            strokeDasharray='4 4'
          />
          {i === colCount - 1 && (
            <line
              x1={col.x + col.width}
              y1={padding}
              x2={col.x + col.width}
              y2={totalHeight}
              stroke={GRID_LINE_COLOR}
              strokeWidth={1}
              strokeDasharray='4 4'
            />
          )}
        </React.Fragment>
      ))}
      {/* Row lines — at top edge of each row */}
      {Array.from({ length: rowCount + 1 }, (_, i) => {
        const y = padding + i * (rowHeight + margin);
        return (
          <line
            key={`row-${i}`}
            x1={padding}
            y1={y}
            x2={containerWidth - padding}
            y2={y}
            stroke={GRID_LINE_COLOR}
            strokeWidth={1}
            strokeDasharray='4 4'
          />
        );
      })}
    </svg>
  );
}

const chartItemsInitialValue = {
  charts: [],
};

const initialEmptyLayouts = {
  lg: [],
  md: [],
  sm: [],
  xs: [],
  xxs: [],
};

export function ChartViewEditor(props: Readonly<ChartEditorProps>) {
  const { onClose, open, onChanges, initialValues, chartViewName } = props;
  const [layouts, setLayouts] = React.useState<Layouts>(initialEmptyLayouts);
  const [selectedIndexEdit, setSelectedIndexEdit] = useState<number | null>(null);
  const [rowHeight, setRowHeight] = useState(30);
  const [currentBreakpoint, setCurrentBreakpoint] = useState('lg');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const gridAreaRef = useRef<HTMLDivElement | null>(null);
  const [emptyDragOver, setEmptyDragOver] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const [gridAreaWidth, setGridAreaWidth] = useState(0);

  const formik = useFormik<{
    charts: ChartItemComponentTypes[];
  }>({
    initialValues: chartItemsInitialValue,
    onSubmit: () => {},
  });

  const [isDragging, setIsDragging] = useState(false);
  const isAtMaxItems = formik.values.charts.length >= MAX_ITEMS;
  const hasCharts = formik.values.charts.length > 0;

  // Dynamic row height + grid width calculation
  const updateRowHeight = useCallback(() => {
    if (!containerRef.current) return;
    const { offsetHeight } = containerRef.current;
    const totalRows = CHART_GRID_CONFIG.TOTAL_ROWS;
    const newRowHeight = Math.max(1, Math.floor(offsetHeight / totalRows));
    setRowHeight(newRowHeight);
    if (gridAreaRef.current) {
      setGridAreaWidth(gridAreaRef.current.offsetWidth);
    }
  }, []);

  const debouncedUpdateRowHeight = React.useMemo(() => debounce(updateRowHeight, 500), [updateRowHeight]);

  useEffect(() => {
    window.addEventListener('resize', debouncedUpdateRowHeight);
    debouncedUpdateRowHeight();

    return () => {
      window.removeEventListener('resize', debouncedUpdateRowHeight);
      debouncedUpdateRowHeight.cancel?.();
    };
  }, [debouncedUpdateRowHeight]);

  // Re-measure grid area when dialog opens or grid toggle changes
  useEffect(() => {
    if (open) {
      // Delay to allow dialog transition to complete
      const timer = setTimeout(() => {
        if (gridAreaRef.current) {
          setGridAreaWidth(gridAreaRef.current.offsetWidth);
        }
        updateRowHeight();
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [open, showGrid, updateRowHeight]);

  const onDrop = (layout: Layout[], layoutItem: Layout, _event: DragEvent) => {
    setIsDragging(false);

    if (_event.dataTransfer) {
      const data = _event.dataTransfer.getData('application/reactgrid');
      if (!data) return;
      const newData = JSON.parse(data);
      const timestampUnix = new Date().getTime();
      const newItemId = `chart_${formik.values.charts.length + timestampUnix}`;
      const newItems = [...formik.values.charts, { id: newItemId, ...newData }];
      formik.setValues({ charts: newItems });

      const newLayoutItem: Layout = {
        i: newItemId,
        x: 0,
        y: Infinity,
        w: 9,
        h: 9,
      };

      setLayouts((prev) => {
        return {
          ...prev,
          lg: [...(prev.lg || []), { ...newLayoutItem }],
          md: [...(prev.md || []), { ...newLayoutItem }],
          sm: [...(prev.sm || []), { ...newLayoutItem }],
          xs: [...(prev.xs || []), { ...newLayoutItem }],
          xxs: [...(prev.xs || []), { ...newLayoutItem }],
        };
      });
    }
  };

  const breakpoints = CHART_GRID_CONFIG.BREAKPOINTS;
  const cols = CHART_GRID_CONFIG.COLS;

  const getRowHeightForBreakpoint = (breakpoint: string) => {
    switch (breakpoint) {
      case 'lg':
        return rowHeight;
      case 'md':
        return Math.max(25, rowHeight * 0.9);
      case 'sm':
        return Math.max(20, rowHeight * 0.8);
      case 'xs':
        return Math.max(15, rowHeight * 0.7);
      case 'xxs':
        return Math.max(12, rowHeight * 0.6);
      default:
        return rowHeight;
    }
  };

  // Grid overlay computed values
  const currentColCount = cols[currentBreakpoint as keyof typeof cols] || 12;
  const currentRowHeight = getRowHeightForBreakpoint(currentBreakpoint);
  const gridMargin = CHART_GRID_CONFIG.MARGIN[0];
  const gridPadding = CHART_GRID_CONFIG.CONTAINER_PADDING[0];
  const containerHeight = containerRef.current?.offsetHeight ?? 0;
  const visibleRowCount =
    containerHeight > 0 ? Math.ceil((containerHeight - RULER_SIZE) / (currentRowHeight + gridMargin)) : 20;

  const handleDragStart = () => {
    if (!isAtMaxItems) {
      setIsDragging(true);
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setEmptyDragOver(false);
  };

  const onLayoutChange = useCallback((newLayout: Layout[], allLayouts: Layouts) => {
    // Use the allLayouts provided by react-grid-layout directly.
    // Only update if the lg layout actually changed to prevent infinite loops
    // caused by regenerating scaled layouts that produce fractional values.
    setLayouts((prev) => {
      const prevLg = prev.lg || [];
      if (
        prevLg.length === newLayout.length &&
        prevLg.every((item, i) => {
          const n = newLayout[i];
          return n && item.i === n.i && item.x === n.x && item.y === n.y && item.w === n.w && item.h === n.h;
        })
      ) {
        return prev; // No change — skip update
      }
      return allLayouts;
    });
  }, []);

  const handleBreakpointChange = (bp: string, cols: number) => {
    setCurrentBreakpoint(bp);
  };

  const handleOnClose = () => {
    formik.setValues(chartItemsInitialValue);
    setLayouts(initialEmptyLayouts);
    setSelectedIndexEdit(null);
    onClose && onClose();
  };

  // ── Edit modal with local draft ──
  const handleOpenEditModal = (idx: number) => {
    setSelectedIndexEdit(idx);
  };

  // Create a draft formik instance for the edit modal
  const draftFormik = useFormik<{ charts: ChartItemComponentTypes[] }>({
    initialValues: { charts: formik.values.charts },
    onSubmit: () => {},
    enableReinitialize: true,
  });

  // When opening the modal, reset draft to current values
  useEffect(() => {
    if (selectedIndexEdit !== null) {
      draftFormik.setValues({ charts: JSON.parse(JSON.stringify(formik.values.charts)) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndexEdit]);

  const handleSaveEditModal = () => {
    // Apply draft values back to the main formik
    formik.setValues({ charts: draftFormik.values.charts });
    setSelectedIndexEdit(null);
  };

  const handleCancelEditModal = () => {
    // Discard draft — original formik is untouched
    setSelectedIndexEdit(null);
  };

  const handleOnButtonSaveClick = () => {
    // Only persist the lg layout — let react-grid-layout handle
    // responsive reflow at runtime for smaller breakpoints.
    // This ensures consistent display across screen sizes.
    onChanges &&
      onChanges({
        charts: formik.values.charts,
        layouts: { lg: layouts.lg || [] },
      });
    handleOnClose();
  };

  const handleOnDeleteChart = (index: number) => {
    setSelectedIndexEdit(null);
    const newCharts = formik.values.charts.filter((_, idx) => idx !== index);
    formik.setValues({ charts: newCharts });
    setLayouts((prev) => {
      const updatedLg = prev.lg.filter((item) => item.i !== formik.values.charts[index].id);
      return { ...prev, lg: updatedLg };
    });
  };

  // ── A. Empty state drop handlers ──
  const handleEmptyDragOver = (e: React.DragEvent) => {
    if (!isAtMaxItems) {
      e.preventDefault();
      setEmptyDragOver(true);
    }
  };

  const handleEmptyDragLeave = () => {
    setEmptyDragOver(false);
  };

  const handleEmptyDrop = (e: React.DragEvent) => {
    setEmptyDragOver(false);
    setIsDragging(false);
    const data = e.dataTransfer.getData('application/reactgrid');
    if (!data) return;
    const newData = JSON.parse(data);
    const timestampUnix = new Date().getTime();
    const newItemId = `chart_${formik.values.charts.length + timestampUnix}`;
    formik.setValues({ charts: [{ id: newItemId, ...newData }] });

    const newLayoutItem: Layout = { i: newItemId, x: 0, y: 0, w: 18, h: 12 };
    setLayouts({
      lg: [newLayoutItem],
      md: [newLayoutItem],
      sm: [newLayoutItem],
      xs: [newLayoutItem],
      xxs: [newLayoutItem],
    });
  };

  useEffect(() => {
    if (initialValues) {
      setLayouts(initialValues?.layouts || initialEmptyLayouts);
      formik.setFieldValue('charts', initialValues?.charts || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <Dialog fullScreen open={open} onClose={handleOnClose} TransitionComponent={Transition}>
      {/* ── Header ── */}
      <Stack px={2} py={1} justifyContent='space-between' direction='row' alignItems='center'>
        <Stack direction='row' gap={1} alignItems='center'>
          <ViewInArOutlinedIcon sx={{ color: '#42BB93' }} />
          <Stack>
            <Typography variant='body1' component='h2' fontWeight='bold' sx={{ color: '#3B4797' }}>
              {chartViewName}
            </Typography>
            <Typography variant='caption' component='h5' fontWeight='bold' sx={{ color: '#999999' }}>
              Chart View Definition
            </Typography>
          </Stack>
        </Stack>
        <Stack direction='row' gap={1} alignItems='center'>
          {/* ── E. Chart count badge ── */}
          <Chip
            size='small'
            label={`${formik.values.charts.length} / ${MAX_ITEMS} charts`}
            color={isAtMaxItems ? 'warning' : 'default'}
            icon={isAtMaxItems ? <BlockOutlinedIcon fontSize='small' /> : undefined}
            variant={isAtMaxItems ? 'filled' : 'outlined'}
            sx={{ fontWeight: 600, fontSize: 12 }}
          />
          {/* ── Grid toggle ── */}
          <Tooltip title={showGrid ? 'Hide grid & rulers' : 'Show grid & rulers'} arrow>
            <IconButton
              onClick={() => setShowGrid((v) => !v)}
              size='small'
              sx={{
                bgcolor: showGrid ? '#3B4797' : 'transparent',
                color: showGrid ? '#fff' : '#98A2AE',
                '&:hover': { bgcolor: showGrid ? '#2e3873' : 'rgba(0,0,0,0.04)' },
                borderRadius: 1,
                width: 32,
                height: 32,
              }}
            >
              {showGrid ? <GridOnOutlinedIcon fontSize='small' /> : <GridOffOutlinedIcon fontSize='small' />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Save the changes you've made to this chart view." arrow>
            <Button onClick={handleOnButtonSaveClick} startIcon={<SaveOutlinedIcon />} variant={'main-table-panel'}>
              Save
            </Button>
          </Tooltip>
          <IconButton onClick={handleOnClose} component='label'>
            <HighlightOffRoundedIcon sx={closeIconStyle} />
          </IconButton>
        </Stack>
      </Stack>
      <Divider />

      {/* ── Chart Type Toolbar ── */}
      <Stack
        direction='row'
        alignItems='center'
        gap={1}
        sx={{
          px: 2,
          py: 0.75,
          bgcolor: '#fafbfc',
          borderBottom: '1px solid #eef0f4',
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#888', mr: 0.5, userSelect: 'none' }}>Add:</Typography>
        {CHART_TYPE_OPTIONS.map((chartType) => (
          <ChartTypeItem
            key={chartType.type}
            icon={chartType.icon}
            chartType={chartType.type}
            title={chartType.title}
            disabled={isAtMaxItems}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          />
        ))}

        {/* Max items warning */}
        <Collapse in={isAtMaxItems} orientation='horizontal'>
          <Stack
            direction='row'
            alignItems='center'
            gap={0.5}
            sx={{
              bgcolor: '#fff3e0',
              border: '1px solid #ffe0b2',
              borderRadius: 1,
              px: 1,
              py: 0.5,
              ml: 1,
              whiteSpace: 'nowrap',
            }}
          >
            <BlockOutlinedIcon sx={{ fontSize: 14, color: '#e65100' }} />
            <Typography sx={{ fontSize: 11, color: '#e65100', fontWeight: 600 }}>Max {MAX_ITEMS} reached</Typography>
          </Stack>
        </Collapse>
      </Stack>

      {/* ── Main Content (grid takes full width) ── */}
      <Stack flex={1} px={2} py={1} overflow='hidden' ref={containerRef}>
        {/* ── Grid Area ── */}
        <Stack flex={1} minWidth={0} ref={gridAreaRef} sx={{ position: 'relative' }}>
          {/* ── Grid Overlay (rulers + lines) ── */}
          {showGrid && gridAreaWidth > 0 && (
            <>
              <RulerCorner />
              <TopRuler
                colCount={currentColCount}
                rowHeight={currentRowHeight}
                rowCount={visibleRowCount}
                margin={gridMargin}
                padding={gridPadding}
                containerWidth={gridAreaWidth - RULER_SIZE}
              />
              <LeftRuler
                colCount={currentColCount}
                rowHeight={currentRowHeight}
                rowCount={visibleRowCount}
                margin={gridMargin}
                padding={gridPadding}
                containerWidth={gridAreaWidth - RULER_SIZE}
              />
            </>
          )}

          {/* ── A. Empty State ── */}
          {!hasCharts && (
            <div
              className={`chart-editor-grid-empty ${emptyDragOver ? 'drag-over' : ''}`}
              style={showGrid ? { marginTop: RULER_SIZE, marginLeft: RULER_SIZE } : undefined}
              onDragOver={handleEmptyDragOver}
              onDragLeave={handleEmptyDragLeave}
              onDrop={handleEmptyDrop}
            >
              <div className='chart-editor-grid-empty-content'>
                <OpenWithOutlinedIcon
                  sx={{ fontSize: 48, color: emptyDragOver ? '#42BB93' : '#bbb', mb: 1, transition: 'color 0.2s' }}
                />
                <Typography
                  variant='h6'
                  sx={{ color: emptyDragOver ? '#42BB93' : '#999', fontWeight: 600, transition: 'color 0.2s' }}
                >
                  {emptyDragOver ? 'Drop here to add' : 'Drag a chart to get started'}
                </Typography>
                <Typography variant='body2' sx={{ color: '#aaa', mt: 0.5, maxWidth: 320 }}>
                  Drag a chart type from the toolbar above and drop it here.
                </Typography>
              </div>
            </div>
          )}

          {/* ── Grid with charts ── */}
          {hasCharts && (
            <div
              style={{
                position: 'relative',
                height: '100%',
                marginTop: showGrid ? RULER_SIZE : 0,
                marginLeft: showGrid ? RULER_SIZE : 0,
              }}
            >
              {showGrid && gridAreaWidth > 0 && (
                <GridLines
                  colCount={currentColCount}
                  rowHeight={currentRowHeight}
                  rowCount={visibleRowCount}
                  margin={gridMargin}
                  padding={gridPadding}
                  containerWidth={gridAreaWidth - RULER_SIZE}
                />
              )}
              <ResponsiveGridLayout
                className={`layout chart-editor-grid ${isDragging && !isAtMaxItems ? 'dragging' : ''}`}
                style={{ height: '100%', width: '100%', position: 'relative', zIndex: 2 }}
                layouts={layouts}
                breakpoints={breakpoints}
                cols={cols}
                rowHeight={currentRowHeight}
                isDroppable={!isAtMaxItems}
                onDrop={onDrop}
                onLayoutChange={onLayoutChange}
                onBreakpointChange={handleBreakpointChange}
                isResizable={true}
                isDraggable={true}
                resizeHandles={['se', 'sw', 'ne', 'nw', 'e', 'w', 's', 'n']}
                margin={[gridMargin, gridMargin]}
                containerPadding={[gridPadding, gridPadding]}
              >
                {formik.values.charts.map((item, idx) => (
                  <div key={item.id} className={selectedIndexEdit === idx ? 'chart-item-selected' : ''}>
                    <ChartItemComponent
                      chartType={item.chartType}
                      title={item.name}
                      isSelected={selectedIndexEdit === idx}
                      onEdit={() => {
                        handleOpenEditModal(idx);
                      }}
                      onDelete={() => {
                        handleOnDeleteChart(idx);
                      }}
                    />
                  </div>
                ))}
              </ResponsiveGridLayout>
            </div>
          )}
        </Stack>
      </Stack>

      {/* ── Chart Edit Modal ── */}
      <Dialog
        open={selectedIndexEdit !== null}
        onClose={handleCancelEditModal}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '80vh',
          },
        }}
      >
        <Stack direction='row' justifyContent='space-between' alignItems='center' px={2.5} pt={2} pb={1}>
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#3B4797' }}>Edit Chart</Typography>
          <IconButton onClick={handleCancelEditModal} size='small'>
            <HighlightOffRoundedIcon sx={closeIconStyle} />
          </IconButton>
        </Stack>
        <Divider />
        <Stack px={2.5} py={2} sx={{ overflowY: 'auto' }}>
          {selectedIndexEdit !== null && (
            <ChartEditorComponentForm index={selectedIndexEdit} formik={draftFormik} onDelete={handleOnDeleteChart} />
          )}
        </Stack>
        <Divider />
        <Stack direction='row' justifyContent='flex-end' gap={1.5} px={2.5} py={1.5}>
          <Button variant='outlined' onClick={handleCancelEditModal} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={handleSaveEditModal}
            sx={{ textTransform: 'none', bgcolor: '#42BB93', '&:hover': { bgcolor: '#38a683' } }}
          >
            Save Changes
          </Button>
        </Stack>
      </Dialog>
    </Dialog>
  );
}
